import { useCallback, useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import {
  createSiteBriefChatSession,
  fetchSessions,
  fetchSiteBriefFontCatalog,
  getPendingSiteBriefChatRequest,
} from '../api';
import type { SessionListItem, SiteBriefConversationReceipt } from '../api.types';
import BlockMenu from '../imports/BlockMenu';
import { LANGUAGE_OPTIONS, useLanguage } from '../components/LanguageDropdown';
import SiteBriefChoiceCard from '../components/SiteBriefChoiceCard';
import SiteBriefSurvey from '../components/SiteBriefSurvey';
import { summaryReviewCopy } from '../utils/siteBriefSummaryReview';
import SiteBriefWorkspacePanels, { BRIEF_STAGES, BriefIcon } from '../components/SiteBriefWorkspacePanels';
import useSiteBriefWorkspace, {
  isBriefTaskPending,
  SITE_BRIEF_AUTOPILOT_NEXT_STEP,
  SITE_BRIEF_AUTOPILOT_STEPS,
} from '../components/hooks/useSiteBriefWorkspace';
import type { SiteBriefInitialSubmission } from '../components/hooks/useSiteBriefWorkspace';
import markdown2html from '../utils/markdown2html';
import { getSiteBriefMessageText } from '../utils/siteBriefMessageText';
import { getSiteBriefSurveyCard } from '../utils/siteBriefSurvey';
import '../styles/site-brief-chat.css';

const STARTER_IDEAS = [
  {
    label: 'A local business',
    detail: 'Turn visitors into your next customers.',
    prompt: 'I need a website for my local business. ',
  },
  {
    label: 'A creative portfolio',
    detail: 'Give your work a place to shine.',
    prompt: 'I want a portfolio website to showcase my work. ',
  },
  {
    label: 'A service or studio',
    detail: 'Make it easy to find and book you.',
    prompt: 'Create a website for my studio and the services I offer. ',
  },
];

const LANGUAGES: Record<string, string> = { en: 'English', ua: 'Ukrainian', es: 'Spanish', ru: 'Russian' };

const getError = (error: unknown): string =>
  error instanceof Error ? error.message : 'Unable to load your site conversations.';

const formatDate = (date: string): string =>
  new Date(date).toLocaleDateString('en', { month: 'short', day: 'numeric' });

function IdeaIllustration() {
  return (
    <div className="sb-idea-art" aria-hidden="true">
      <div className="sb-art-orbit sb-art-orbit-one" />
      <div className="sb-art-orbit sb-art-orbit-two" />
      <div className="sb-art-note">
        <BriefIcon name="chat" size={18} />
        <span>Your idea</span>
        <i />
        <i />
      </div>
      <div className="sb-art-browser">
        <div className="sb-art-browser-top">
          <i />
          <i />
          <i />
          <span />
        </div>
        <div className="sb-art-browser-body">
          <div className="sb-art-nav">
            <b />
            <span />
            <span />
            <span />
          </div>
          <div className="sb-art-hero">
            <div>
              <i />
              <i />
              <p />
              <p />
              <b />
            </div>
            <div className="sb-art-image">
              <span />
              <span />
            </div>
          </div>
          <div className="sb-art-cards">
            <i />
            <i />
            <i />
          </div>
        </div>
      </div>
      <div className="sb-art-spark">
        <BriefIcon size={22} />
      </div>
      <div className="sb-art-done">
        <BriefIcon name="check" size={13} /> A website that feels like you
      </div>
    </div>
  );
}

function NewSite({ token, onCreated, onSubmitted }: {
  token: string;
  onCreated: (session: SessionListItem, messageId: string) => void;
  onSubmitted: (sessionId: string, error: string | null, receipt: SiteBriefConversationReceipt | null) => void;
}) {
  const { selectedLanguage } = useLanguage();
  const [name, setName] = useState('');
  const [prompt, setPrompt] = useState(() => sessionStorage.getItem('site-builder-idea') || '');
  const [language, setLanguage] = useState<string>(selectedLanguage);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const promptRef = useRef<HTMLTextAreaElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const creatingRef = useRef(false);

  useEffect(() => {
    sessionStorage.setItem('site-builder-idea', prompt);
  }, [prompt]);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (creatingRef.current) {
      return;
    }
    promptRef.current?.setCustomValidity(prompt.trim() ? '' : 'Please describe what you would like to create.');
    nameRef.current?.setCustomValidity(name.trim() ? '' : 'Please enter a session name to start your conversation.');
    if (!event.currentTarget.reportValidity()) {
      return;
    }
    creatingRef.current = true;
    setCreating(true);
    setError(null);
    try {
      const { session, submissionError, receipt } = await createSiteBriefChatSession(
        token, name.trim(), language, prompt.trim(),
        (created, messageId) => {
          sessionStorage.removeItem('site-builder-idea');
          onCreated({
            ...created,
            is_active: true,
            is_ended: false,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          }, messageId);
        },
      );
      onSubmitted(session.id, submissionError, receipt);
    } catch (failure) {
      setError(getError(failure));
    } finally {
      creatingRef.current = false;
      setCreating(false);
    }
  };

  return (
    <div className="sb-new-site">
      <div className="sb-new-intro">
        <span className="sb-kicker">
          <BriefIcon size={14} /> A LITTLE CONVERSATION. A WHOLE NEW WEBSITE.
        </span>
        <h1>
          Big ideas start
          <br />
          with a <span>simple hello.</span>
        </h1>
        <p>
          Tell us what you have in mind. Shape the details with Evo,
          <br className="sb-desktop-break" /> then turn your conversation into a website.
        </p>
      </div>
      <IdeaIllustration />
      <form
        className="sb-idea-form"
        noValidate
        onSubmit={(event) => {
          void submit(event);
        }}
      >
        <label htmlFor="sb-initial-idea">
          What would you like to create? <span className="sb-required-label">(required)</span>
        </label>
        <textarea
          id="sb-initial-idea"
          ref={promptRef}
          value={prompt}
          onChange={(event) => {
            event.target.setCustomValidity('');
            setPrompt(event.target.value);
          }}
          disabled={creating}
          maxLength={20000}
          rows={4}
          placeholder="A warm, minimal website for my ceramics studio. I’d love to show my work, share upcoming workshops, and help people get in touch…"
          required
        />
        <div className="sb-idea-options">
          <label className="sb-session-name">
            <span className="sb-sr-only">Session Name (required)</span>
            <BriefIcon name="file" size={16} />
            <input
              ref={nameRef}
              aria-label="Session Name (required)"
              placeholder="Session Name (required)"
              value={name}
              onChange={(event) => {
                event.target.setCustomValidity('');
                setName(event.target.value);
              }}
              maxLength={100}
              disabled={creating}
              required
            />
          </label>
          <label>
            <span className="sb-sr-only">Conversation language</span>
            <select
              aria-label="Conversation language"
              value={language}
              onChange={(event) => setLanguage(event.target.value)}
              disabled={creating}
            >
              {LANGUAGE_OPTIONS.map((value) => (
                <option key={value} value={value}>
                  {LANGUAGES[value]}
                </option>
              ))}
            </select>
          </label>
          <button className="sb-button sb-button-primary" type="submit" disabled={creating}>
            {creating ? <span className="sb-spinner" /> : <BriefIcon size={17} />}
            {creating ? 'Starting your conversation…' : 'Start conversation'}
            {!creating && <BriefIcon name="chevron" size={16} />}
          </button>
        </div>
        {error && (
          <p className="sb-error" role="alert">
            {error}
          </p>
        )}
      </form>
      <div className="sb-idea-starters">
        <span>NEED A STARTING POINT?</span>
        <div>
          {STARTER_IDEAS.map((idea, index) => (
            <button
              key={idea.label}
              disabled={creating}
              onClick={() => {
                setPrompt(idea.prompt);
                promptRef.current?.focus();
              }}
            >
              <span className={`sb-starter-icon sb-starter-${index}`}>
                <BriefIcon name={index === 1 ? 'file' : index === 2 ? 'spark' : 'globe'} size={19} />
              </span>
              <strong>{idea.label}</strong>
              <p>{idea.detail}</p>
              <BriefIcon name="chevron" size={15} />
            </button>
          ))}
        </div>
      </div>
      <div className="sb-new-footnote">
        <span>
          01 <b>Share your idea</b>
        </span>
        <i />
        <span>
          02 <b>Shape your brief</b>
        </span>
        <i />
        <span>
          03 <b>Build your website</b>
        </span>
      </div>
    </div>
  );
}

function Conversation({
  token,
  sessionId,
  onSessionLoaded,
  initialSubmission,
}: {
  token: string;
  sessionId: string;
  onSessionLoaded: (name: string) => void;
  initialSubmission: SiteBriefInitialSubmission | null;
}) {
  const {
    workspace,
    loading,
    mutating,
    building,
    error,
    syncError,
    pendingMessage,
    send,
    buildSite,
    stopTask,
    summaryDraft,
    changeSummary,
    discardSummary,
    saveSummary,
    refresh,
    autopilot,
    autopilotError,
    resumeAutopilot,
  } = useSiteBriefWorkspace(token, sessionId, initialSubmission);
  const recoveredRequest = useRef(getPendingSiteBriefChatRequest(sessionId));
  const [draft, setDraft] = useState(
    () =>
      getPendingSiteBriefChatRequest(sessionId)?.message ||
      sessionStorage.getItem(`site-builder-draft:${sessionId}`) ||
      '',
  );
  const [mobilePanel, setMobilePanel] = useState<'chat' | 'details'>('chat');
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  // Continue chat from the sidebar briefly highlights the message box it focuses.
  const [composerCalled, setComposerCalled] = useState(false);
  const composerCallTimer = useRef<number>();
  useEffect(() => () => window.clearTimeout(composerCallTimer.current), []);
  const pending = isBriefTaskPending(workspace?.task) || Boolean(workspace?.metadata.active_task_id) || Boolean(initialSubmission?.pending);
  const busy = pending || mutating;
  const active = Boolean(workspace?.session.is_active && !workspace.session.is_ended && !workspace.session.is_deleted);
  const canChat = workspace?.metadata.status === 'gather_requirements' && active;
  const visibleMessages =
    workspace?.session.messages.filter(
      (message) => message.role === 'user' || (message.role === 'assistant' && message.content.trim()),
    ) ?? [];
  const pendingIsPersisted =
    workspace?.task?.user_message_id &&
    visibleMessages.some((message) => message.id === workspace.task?.user_message_id);
  // Only the latest reply's question is open, in the panel above the message box; earlier ones read as text.
  const latestMessage = visibleMessages[visibleMessages.length - 1];
  const questionMessage =
    latestMessage?.role === 'assistant' &&
    latestMessage.site_brief_survey &&
    !(pendingMessage && !pendingIsPersisted)
      ? latestMessage
      : null;
  const questionCard = questionMessage?.site_brief_survey ? getSiteBriefSurveyCard(questionMessage.site_brief_survey) : null;
  const sessionName = workspace?.session.name;

  useEffect(() => {
    sessionStorage.setItem(`site-builder-draft:${sessionId}`, draft);
  }, [draft, sessionId]);

  useEffect(() => {
    if (workspace?.task?.operation === 'conversation' && pendingMessage && workspace.task.state !== 'failed') {
      setDraft((current) => (current.trim() === pendingMessage ? '' : current));
    }
  }, [workspace?.task?.id, workspace?.task?.operation, workspace?.task?.state, pendingMessage]);

  useEffect(() => {
    if (initialSubmission?.error) {
      setDraft((current) => current || sessionStorage.getItem(`site-builder-draft:${sessionId}`) || '');
    }
  }, [initialSubmission, sessionId]);

  useEffect(() => {
    const request = recoveredRequest.current;
    if (request && workspace?.session.messages.some((message) => message.id === request.client_request_id)) {
      setDraft((current) => current.trim() === request.message ? '' : current);
      if (!initialSubmission?.pending) {
        recoveredRequest.current = null;
      }
    }
  }, [workspace?.session.messages, initialSubmission]);

  useEffect(() => {
    if (sessionName) {
      onSessionLoaded(sessionName);
    }
  }, [sessionName, onSessionLoaded]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [visibleMessages.length, pending, pendingMessage]);

  const loadFontCatalog = useCallback(() => fetchSiteBriefFontCatalog(token), [token]);

  const chooseOption = async (answer: string) => {
    if (busy || !canChat) {
      return false;
    }
    return send(answer);
  };

  const submit = async (event?: FormEvent) => {
    event?.preventDefault();
    if (!draft.trim() || busy || !canChat) {
      return;
    }
    const message = draft.trim();
    if (await send(message)) {
      setDraft('');
      inputRef.current?.focus();
    }
  };

  if (loading) {
    return (
      <div className="sb-workspace-loading" role="status">
        <span className="sb-spinner" />
        <p>Opening your site conversation…</p>
      </div>
    );
  }
  if (!workspace) {
    return (
      <div className="sb-workspace-loading">
        <BriefIcon name="chat" size={30} />
        <h2>We couldn’t open this conversation</h2>
        <p role="alert">{syncError}</p>
        <button
          className="sb-button sb-button-secondary"
          onClick={() => {
            void refresh();
          }}
        >
          Try again
        </button>
      </div>
    );
  }
  const summaryCopy = summaryReviewCopy(workspace.metadata.presentation?.language ?? workspace.session.language ?? 'en');
  const askAboutChangesInput = workspace.metadata.presentation?.ask_about_changes_input;
  // The brief sidebar stays hidden until Evo finishes the interview; later stages always show it.
  const showDetails = Boolean(workspace.metadata.is_gather_requirements_done) || workspace.metadata.status !== 'gather_requirements';
  const visiblePanel = showDetails ? mobilePanel : 'chat';
  const buildBlocked = !active || busy || autopilot || Boolean(syncError) || Boolean(getPendingSiteBriefChatRequest(sessionId));
  const continueChat = () => {
    setMobilePanel('chat');
    setComposerCalled(true);
    window.clearTimeout(composerCallTimer.current);
    composerCallTimer.current = window.setTimeout(() => setComposerCalled(false), 1500);
    // Wait for the chat panel to show on small screens before moving the focus into it.
    window.requestAnimationFrame(() => {
      const input = inputRef.current;
      input?.focus();
      input?.setSelectionRange(input.value.length, input.value.length);
    });
  };
  const askAboutChanges = () => {
    if (!askAboutChangesInput || !canChat || busy || syncError) return;
    // Keep a draft the person has already started; repeated clicks only return focus to it.
    setDraft((current) => current.trim() ? current : askAboutChangesInput);
    continueChat();
  };
  const stageIndex = BRIEF_STAGES.findIndex((stage) => stage.status === workspace.metadata.status);
  const briefStatus = workspace.metadata.status;
  const taskRunning = isBriefTaskPending(workspace.task) && workspace.task?.operation !== 'conversation';
  const siteBuilt = briefStatus === 'fulfill_vibe_blocks' && workspace.metadata.creation?.fulfillment_status === 'completed';
  // The step running now, or the one that runs next from the current brief status.
  const runningStep = taskRunning
    ? SITE_BRIEF_AUTOPILOT_STEPS.findIndex((step) => step.operation === (workspace.task?.build?.current_stage ?? workspace.task?.operation))
    : -1;
  const nextStep = SITE_BRIEF_AUTOPILOT_NEXT_STEP[briefStatus];
  const currentStepIndex =
    runningStep >= 0
      ? runningStep
      : siteBuilt
        ? SITE_BRIEF_AUTOPILOT_STEPS.length
        : SITE_BRIEF_AUTOPILOT_STEPS.findIndex((step) => step.operation === nextStep);
  const showBuildProgress = autopilot || Boolean(autopilotError) || taskRunning
    || workspace.task?.operation === 'build_site' || !['init', 'gather_requirements'].includes(briefStatus);
  const currentStage = BRIEF_STAGES[stageIndex];

  return (
    <div className="sb-workspace">
      <div className="sb-workspace-header">
        <div>
          <span className="sb-eyebrow">SITE CONVERSATION</span>
          <h1>{workspace.session.name}</h1>
          {workspace.metadata.can_stop && <button type="button" className="sb-button sb-button-secondary" disabled={mutating} onClick={() => void stopTask()}>Stop</button>}
          {workspace.task?.state === 'cancelling' && <p role="status">Stopping…</p>}
          {workspace.task?.state === 'cancelled' && <p role="status">Stopped. Your saved progress is kept.</p>}
        </div>
        <div className="sb-workspace-header-actions">
          <span className="sb-status-pill">
            <i />
            {currentStage.label}
          </span>
          <button
            className="sb-icon-button"
            title="Refresh conversation"
            aria-label="Refresh conversation"
            disabled={mutating}
            onClick={() => {
              void refresh();
            }}
          >
            <BriefIcon name="refresh" size={17} />
          </button>
        </div>
      </div>
      <ol className="sb-steps" aria-label="Website creation stages">
        {BRIEF_STAGES.map((stage, index) => (
          <li
            key={stage.status}
            className={index === stageIndex ? 'is-current' : index < stageIndex ? 'is-done' : ''}
            aria-current={index === stageIndex ? 'step' : undefined}
          >
            <span>{index < stageIndex ? <BriefIcon name="check" size={12} /> : index + 1}</span>
            {stage.label}
          </li>
        ))}
      </ol>
      {showDetails && (
        <div className="sb-mobile-tabs">
          <button className={mobilePanel === 'chat' ? 'is-active' : ''} onClick={() => setMobilePanel('chat')}>
            <BriefIcon name="chat" size={16} /> Conversation
          </button>
          <button className={mobilePanel === 'details' ? 'is-active' : ''} onClick={() => setMobilePanel('details')}>
            <BriefIcon name="file" size={16} /> Brief & build
          </button>
        </div>
      )}
      {syncError && (
        <div className="sb-sync-error" role="status">
          Updates paused: {syncError}{' '}
          <button
            onClick={() => {
              void refresh();
            }}
          >
            Reconnect
          </button>
        </div>
      )}
      {(error || workspace.task?.state === 'failed') && (
        <div className="sb-workspace-alert" role="alert">
          {error ||
            workspace.task?.error ||
            'This step couldn’t be completed. Please retry; your saved progress is kept.'}
          {workspace.task?.state === 'failed' && workspace.task.operation === 'conversation' && (
            <button
              className="sb-text-button"
              onClick={() => {
                setDraft(
                  visibleMessages.find((message) => message.id === workspace.task?.user_message_id)?.content ||
                    pendingMessage ||
                    '',
                );
                setMobilePanel('chat');
                inputRef.current?.focus();
              }}
            >
              Use this message again
            </button>
          )}
        </div>
      )}
      <div className={`sb-workspace-columns sb-show-${visiblePanel}${showDetails ? '' : ' sb-without-details'}`}>
        <section className="sb-conversation" aria-label="Site brief conversation">
          <div className="sb-conversation-top">
            <span className="sb-evo-avatar">
              <BriefIcon size={18} />
            </span>
            <div>
              <strong>Evo</strong>
              <span>Your website co-creator</span>
            </div>
            <span className="sb-text-only">A space for your next big idea</span>
          </div>
          <div
            className="sb-messages"
            role="log"
            aria-label="Conversation messages"
            aria-live="polite"
            aria-relevant="additions text"
          >
            <div className="sb-conversation-start">
              <span>THE START OF SOMETHING GOOD</span>
            </div>
            {visibleMessages.length === 0 && (
              <div className="sb-welcome-message">
                <span className="sb-evo-avatar">
                  <BriefIcon size={19} />
                </span>
                <div>
                  <h2>Let’s make it yours.</h2>
                  <p>
                    {workspace.metadata.status === 'init'
                      ? 'This brief uses the manual workflow. Start gathering from the Site briefs board to open chat.'
                      : 'Tell me about your business, who you want to reach, and what your website should do. We’ll build the brief together.'}
                  </p>
                </div>
              </div>
            )}
            {visibleMessages.map((message) => {
              return (
                <article className={`sb-message sb-message-${message.role}`} key={message.id}>
                  <div className="sb-message-author">
                    {message.role === 'assistant' && <BriefIcon size={14} />}
                    <strong>{message.role === 'user' ? 'You' : 'Evo'}</strong>
                    <time dateTime={message.timestamp}>
                      {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </time>
                  </div>
                  {message.role === 'user' ? (
                    <div className="sb-message-content">{message.content}</div>
                  ) : (
                    <div
                      className="sb-message-content"
                      dangerouslySetInnerHTML={{ __html: markdown2html(message === questionMessage ? message.content : getSiteBriefMessageText(message)) }}
                    />
                  )}
                </article>
              );
            })}
            {pendingMessage && !pendingIsPersisted && (
              <article className="sb-message sb-message-user sb-message-pending">
                <div className="sb-message-author">
                  <strong>You</strong>
                  <span>
                    {mutating
                      ? 'Sending…'
                      : error
                        ? 'Checking delivery'
                        : workspace.task?.state === 'failed'
                          ? 'Step failed'
                          : 'Sent'}
                  </span>
                </div>
                <div className="sb-message-content">{pendingMessage}</div>
              </article>
            )}
            {pending && (
              <div className="sb-working" role="status">
                <span className="sb-evo-avatar">
                  <BriefIcon size={18} />
                </span>
                <div>
                  <strong>
                    {initialSubmission?.pending || workspace.task?.operation === 'conversation'
                      ? 'Evo is shaping your brief'
                      : workspace.task?.state === 'queued'
                        ? 'Your next step is queued'
                        : 'Your website is taking shape'}
                  </strong>
                  <p>You can leave this page. Your progress is saved.</p>
                </div>
                <span className="sb-thinking">
                  <i />
                  <i />
                  <i />
                </span>
              </div>
            )}
            <div ref={bottomRef} />
          </div>
          <div className="sb-composer-area">
            {showBuildProgress && (
              <div className="sb-build-progress" role="status" aria-live="polite">
                <strong className="sb-build-progress__title">
                  {workspace.task?.state === 'cancelling' ? 'Stopping…' : workspace.task?.state === 'cancelled' ? 'Stopped' : siteBuilt
                    ? 'Your site is ready'
                    : autopilotError
                      ? 'Building stopped'
                      : autopilot || taskRunning
                        ? 'Building your site…'
                        : 'Your site is partly built'}
                </strong>
                <ol className="sb-build-progress__steps">
                  {SITE_BRIEF_AUTOPILOT_STEPS.map((step, index) => {
                    const persistedState = workspace.task?.build?.stages.find(item => item.stage === step.operation)?.state;
                    const savedState = workspace.task?.state === 'cancelled' && persistedState !== 'completed' ? 'pending' : persistedState;
                    const state = savedState
                      ? ({ completed: 'done', running: 'active', failed: 'failed', pending: 'todo' } as const)[savedState]
                      : siteBuilt || index < currentStepIndex
                        ? 'done'
                        : index === currentStepIndex && (autopilot || taskRunning)
                          ? 'active'
                          : index === currentStepIndex && autopilotError
                            ? 'failed'
                            : 'todo';
                    return (
                      <li key={step.operation} className={`is-${state}`}>
                        <span className="sb-build-progress__mark" aria-hidden="true">
                          {state === 'done' ? '✓' : state === 'failed' ? '!' : state === 'active' ? '' : index + 1}
                        </span>
                        {step.label}
                      </li>
                    );
                  })}
                </ol>
                {workspace.task?.build && <p className="sb-build-progress__note">
                  {workspace.task.build.blocks.completed} / {workspace.task.build.blocks.total} content blocks saved
                  {workspace.task.build.blocks.failed > 0 && ` · ${workspace.task.build.blocks.failed} need a retry`}
                  {workspace.task.build.next_retry_at && ' · Retrying automatically…'}
                </p>}
                {siteBuilt && <p className="sb-build-progress__note">The website details are in the panel on the right.</p>}
                {autopilotError && <p className="sb-build-progress__error">{autopilotError}</p>}
                {!siteBuilt && !autopilot && !taskRunning && (
                  <button type="button" className="sb-button sb-button-primary" disabled={busy || Boolean(syncError) || (!workspace.metadata.can_retry && !workspace.metadata.can_build)} onClick={resumeAutopilot}>
                    <BriefIcon size={14} />
                    {autopilotError ? 'Try again' : 'Continue building'}
                  </button>
                )}
              </div>
            )}
            {!canChat && !showBuildProgress && (
              <div className="sb-chat-stage-note">
                <BriefIcon name="file" size={17} />
                <p>
                  {!active
                    ? 'This conversation is read-only because the session has ended or is inactive.'
                    : workspace.metadata.status === 'init'
                      ? 'This brief uses the manual workflow. Open the Site briefs board to start gathering.'
                      : 'Chat is paused during this build stage. Your conversation is saved.'}
                </p>
              </div>
            )}
            {/* The question panel stays outside the form, so Enter in its own-answer field never sends the composer. */}
            {questionMessage && questionCard && (
              <SiteBriefChoiceCard
                key={questionMessage.id}
                choice={questionCard}
                fallbackLanguage={workspace.session.language}
                loadFontCatalog={loadFontCatalog}
                disabled={busy || !canChat}
                onSubmit={(answer) => void chooseOption(answer)}
              />
            )}
            {questionMessage?.site_brief_survey && !questionCard && (
              <SiteBriefSurvey
                key={questionMessage.id}
                survey={questionMessage.site_brief_survey}
                storageKey={`site-brief-survey:${workspace.session.id}:${questionMessage.id}`}
                disabled={busy || !canChat}
                onSubmit={chooseOption}
              />
            )}
            <form
              className={`sb-composer ${!canChat ? 'is-disabled' : ''}${composerCalled ? ' is-called' : ''}`}
              onSubmit={(event) => {
                void submit(event);
              }}
            >
              {askAboutChangesInput && (
                <button
                  type="button"
                  className="sb-text-button sb-ask-changes"
                  disabled={!canChat || busy || Boolean(syncError)}
                  onClick={askAboutChanges}
                >
                  <BriefIcon name="chat" size={14} />
                  {summaryCopy.askAboutChanges}
                </button>
              )}
              <label className="sb-sr-only" htmlFor="sb-chat-message">
                Message Evo about your website
              </label>
              <textarea
                ref={inputRef}
                id="sb-chat-message"
                value={initialSubmission?.pending ? '' : draft}
                onChange={(event) => setDraft(event.target.value)}
                disabled={!canChat || busy || Boolean(syncError)}
                maxLength={20000}
                rows={3}
                placeholder={
                  canChat
                    ? 'Tell Evo a little more about your website…'
                    : 'Your conversation will be here when you need it.'
                }
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
                    event.preventDefault();
                    void submit();
                  }
                }}
              />
              <div className="sb-composer-bottom">
                <span>
                  <BriefIcon name="chat" size={14} /> Every detail makes it more you.
                </span>
                <div className="sb-composer-actions">
                  <button
                    type="submit"
                    className="sb-button sb-button-secondary"
                    disabled={!canChat || busy || !draft.trim() || Boolean(syncError)}
                  >
                    {mutating && !building ? <span className="sb-spinner" /> : <BriefIcon name="chat" size={14} />}
                    Continue chat
                  </button>
                  {/* Before the interview is finished this opens the brief; building itself lives in the sidebar. */}
                  {(workspace.metadata.can_build || workspace.metadata.can_retry) && !showDetails && (
                    <button
                      type="button"
                      className="sb-button sb-button-primary"
                      disabled={!workspace.metadata.presentation?.summary.trim() || buildBlocked}
                      onClick={() => {
                        void buildSite();
                      }}
                    >
                      <BriefIcon size={14} />
                      {summaryCopy.review}
                    </button>
                  )}
                </div>
              </div>
            </form>
            <div className="sb-composer-hint">
              <span>Your brief saves as you chat.</span>
              <span>Enter to send · Shift + Enter for a new line</span>
            </div>
          </div>
        </section>
        {showDetails && (
          <SiteBriefWorkspacePanels
            workspace={workspace}
            summaryDraft={summaryDraft?.after} summaryDirty={Boolean(summaryDraft)}
            summaryDisabled={busy || !canChat || Boolean(syncError)}
            onSummaryChange={changeSummary} onSummarySave={() => void saveSummary()} onSummaryDiscard={discardSummary}
            onBuild={() => void buildSite()} buildDisabled={buildBlocked} building={building}
            onContinueChat={canChat ? continueChat : undefined}
          />
        )}
      </div>
    </div>
  );
}

export default function SiteBriefChatPage() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const sessionId = params.get('session');
  const token = localStorage.getItem('authToken') || '';
  const [sessions, setSessions] = useState<SessionListItem[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [showSessions, setShowSessions] = useState(false);
  const [reload, setReload] = useState(0);
  const [initialSubmission, setInitialSubmission] = useState<SiteBriefInitialSubmission | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoadingSessions(true);
    fetchSessions(token, 'vibe_site')
      .then((items) => {
        if (!cancelled) {
          setSessions(
            items
              .filter((item) => item.type === 'vibe_site')
              .sort((a, b) => Date.parse(b.updated_at) - Date.parse(a.updated_at)),
          );
          setListError(null);
        }
      })
      .catch((failure) => {
        if (!cancelled) {
          setListError(getError(failure));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingSessions(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [reload, token]);

  const sessionLoaded = useCallback(
    (name: string) => {
      setSessions((current) =>
        current.map((session) => (session.id === sessionId && session.name !== name ? { ...session, name } : session)),
      );
    },
    [sessionId],
  );

  const selectSession = (id: string | null) => {
    setParams(id ? { session: id } : {});
    setShowSessions(false);
  };
  const filtered = sessions.filter((session) => session.name.toLowerCase().includes(search.trim().toLowerCase()));

  return (
    <div className="sb-page">
      <header className="sb-global-header">
        <BlockMenu
          onLogout={() => {
            logout();
            navigate('/login');
          }}
          showSessionControls={false}
        />
      </header>
      <div className="sb-app">
        <button
          className="sb-sessions-toggle"
          onClick={() => setShowSessions((current) => !current)}
          aria-expanded={showSessions}
          aria-controls="sb-sessions"
        >
          <BriefIcon name="chat" size={17} /> Your site conversations <BriefIcon name="chevron" size={14} />
        </button>
        <aside
          id="sb-sessions"
          className={`sb-sidebar ${showSessions ? 'is-open' : ''}`}
          aria-label="Site conversations"
        >
          <div className="sb-sidebar-title">
            <span className="sb-brand-symbol">
              <BriefIcon size={19} />
            </span>
            <div>
              <strong>Site builder</strong>
              <span>Ideas into websites</span>
            </div>
          </div>
          <button className="sb-button sb-button-primary sb-wide sb-new-button" onClick={() => selectSession(null)}>
            <BriefIcon name="plus" size={17} /> New session
          </button>
          <label className="sb-search">
            <BriefIcon name="search" size={16} />
            <input
              aria-label="Search site conversations"
              placeholder="Find a conversation"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>
          <div className="sb-sidebar-section-heading">
            <span>YOUR CONVERSATIONS</span>
            <span>{sessions.length}</span>
          </div>
          <div className="sb-session-list">
            {loadingSessions && (
              <p className="sb-sidebar-message" role="status">
                Loading conversations…
              </p>
            )}
            {listError && (
              <div className="sb-sidebar-message" role="alert">
                <p>{listError}</p>
                <button className="sb-text-button" onClick={() => setReload((current) => current + 1)}>
                  Try again
                </button>
              </div>
            )}
            {!loadingSessions && !listError && filtered.length === 0 && (
              <p className="sb-sidebar-message">
                {search
                  ? 'No matching conversations.'
                  : 'Your next website starts with a conversation. Create one above.'}
              </p>
            )}
            {filtered.map((session) => (
              <button
                className={`sb-session ${session.id === sessionId ? 'is-active' : ''}`}
                key={session.id}
                onClick={() => selectSession(session.id)}
                aria-current={session.id === sessionId ? 'page' : undefined}
              >
                <span className="sb-session-icon">
                  <BriefIcon name="chat" size={17} />
                </span>
                <span>
                  <strong>{session.name}</strong>
                  <small>
                    {!session.is_active || session.is_ended ? 'Read-only' : 'Site brief'}
                    <i />
                    {formatDate(session.updated_at)}
                  </small>
                </span>
                <BriefIcon name="chevron" size={13} />
              </button>
            ))}
          </div>
          <div className="sb-sidebar-footer">
            <div>
              <BriefIcon name="file" size={17} />
              <strong>A little more control?</strong>
            </div>
            <p>Explore all requirements and build stages on the Site briefs board.</p>
            <Link to="/site-briefs">
              Open Site briefs <BriefIcon name="chevron" size={13} />
            </Link>
          </div>
        </aside>
        <main className="sb-main">
          {sessionId ? (
            <Conversation
              key={sessionId}
              token={token}
              sessionId={sessionId}
              onSessionLoaded={sessionLoaded}
              initialSubmission={initialSubmission?.sessionId === sessionId ? initialSubmission : null}
            />
          ) : (
            <NewSite
              token={token}
              onCreated={(session, messageId) => {
                setInitialSubmission({ sessionId: session.id, messageId, error: null, pending: true });
                setSessions((current) => [session, ...current]);
                selectSession(session.id);
              }}
              onSubmitted={(id, error, receipt) => {
                setInitialSubmission((current) => current?.sessionId === id ? { ...current, error, receipt, pending: false } : current);
              }}
            />
          )}
        </main>
      </div>
    </div>
  );
}
